import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  List,
  RecordContextProvider,
  ReferenceField,
  TextField,
  TopToolbar,
  useGetList,
  useListContext,
  useNotify,
} from 'react-admin';
import { JsonComparator } from '../../utils/json-comparator';
import { AnalysisQueueImage } from '../form-submissions/DocumentShow';

// Initialize JSON comparator
const jsonComparator = new JsonComparator();

// Enhanced JSON comparison display with highlighting
const ComparisonJsonView = ({
  src,
  highlightMissing = [],
  highlightExtra = [],
  highlightDifferent = [],
  type = 'expected',
}: {
  src: any;
  highlightMissing?: string[];
  highlightExtra?: string[];
  highlightDifferent?: string[];
  type?: 'expected' | 'actual';
}) => {
  const renderJsonWithHighlight = (obj: any, prefix = '', level = 0) => {
    if (obj === null || obj === undefined) {
      return <span style={{ color: '#999' }}>null</span>;
    }

    if (typeof obj !== 'object') {
      return <span style={{ color: '#d14' }}>{JSON.stringify(obj)}</span>;
    }

    if (Array.isArray(obj)) {
      return (
        <span>
          [
          {obj.map((item, index) => (
            <div key={index} style={{ marginLeft: `${(level + 1) * 20}px` }}>
              {renderJsonWithHighlight(item, `${prefix}[${index}]`, level + 1)}
              {index < obj.length - 1 && ','}
            </div>
          ))}
          ]
        </span>
      );
    }

    return (
      <span>
        {'{'}
        {Object.entries(obj).map(([key, value], index, array) => {
          const fieldPath = prefix ? `${prefix}.${key}` : key;
          let backgroundColor = 'transparent';

          if (type === 'expected' && highlightMissing.includes(fieldPath)) {
            backgroundColor = '#ffcdd2'; // Light red for missing in actual
          } else if (type === 'actual' && highlightExtra.includes(fieldPath)) {
            backgroundColor = '#c8e6c9'; // Light green for extra in actual
          } else if (type === 'actual' && highlightDifferent.includes(fieldPath)) {
            backgroundColor = '#bbdefb'; // Light blue for different values
          }

          return (
            <div
              key={key}
              style={{
                marginLeft: `${(level + 1) * 20}px`,
                backgroundColor,
                padding: backgroundColor !== 'transparent' ? '2px 4px' : '0',
                borderRadius: backgroundColor !== 'transparent' ? '3px' : '0',
                margin: backgroundColor !== 'transparent' ? '1px 0' : '0',
              }}
            >
              <span style={{ color: '#0451A5' }}>"{key}"</span>: {renderJsonWithHighlight(value, fieldPath, level + 1)}
              {index < array.length - 1 && ','}
            </div>
          );
        })}
        {'}'}
      </span>
    );
  };

  return (
    <Box
      sx={{
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: '11px',
        lineHeight: 1.4,
        overflow: 'auto',
        maxHeight: '180px',
        padding: '8px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
      }}
    >
      {renderJsonWithHighlight(src)}
    </Box>
  );
};

export enum SettingKeys {
  // prompt = 'prompt',
  // form_prompt = 'form_prompt',
  form_extract_prompt = 'form_extract_prompt',
  // ai_service = 'ai_service',
  // should_retry_claude = 'should_retry_claude',
  // limit_request_form_extract_free = 'limit_request_form_extract_free',
  // view_data_host = 'view_data_host',
  form_extract_prompt_2_1 = 'form_extract_prompt_2_1',
}

const REQUEST_ADMIN_ANALYST = gql`
  mutation RequestAdminAdminAnalyst($params: request_admin_admin_analyst_input!) {
    request_admin_admin_analyst(params: $params) {
      error
    }
  }
`;

const REQUEST_UPDATE_ADMIN_ANALYST = gql`
  mutation RequestUpdateAdminAnalyst($params: request_update_admin_analyst_input!) {
    request_update_admin_analyst(params: $params) {
      error
    }
  }
`;

// Context for selected prompt
const SelectedPromptContext = createContext<SettingKeys>(SettingKeys.form_extract_prompt_2_1);

const SafeAnalysisQueueImage = () => {
  try {
    return <AnalysisQueueImage />;
  } catch (error) {
    return (
      <Box
        sx={{
          width: 120,
          height: 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          画像なし
        </Typography>
      </Box>
    );
  }
};

const JSONEditor = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          height: '300px',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          fontSize: '12px',
          padding: '12px',
          border: 'none',
          outline: 'none',
          resize: 'vertical',
          backgroundColor: '#f8f9fa',
          lineHeight: '1.4',
        }}
        spellCheck={false}
      />
    </Box>
  );
};

const ResultCard = ({ record }: { record: any }) => {
  const notify = useNotify();
  const [openDialog, setOpenDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [editedExpected, setEditedExpected] = useState('');
  const selectedPrompt = useContext(SelectedPromptContext);
  const [requestAdminAnalyst, { loading: mutationLoading }] = useMutation(REQUEST_ADMIN_ANALYST);
  const [requestUpdateAdminAnalyst, { loading: updateMutationLoading }] = useMutation(REQUEST_UPDATE_ADMIN_ANALYST);

  const {
    data: adminAnalyses,
    error: adminAnalysisError,
    refetch,
  } = useGetList(
    'admin_analysis',
    {
      filter: { form_submission_id: record.id, form_extract_key: selectedPrompt },
      sort: { field: 'id', order: 'DESC' },
    },
    { enabled: !!record.id, retry: false },
  );

  const adminAnalysis = adminAnalyses?.[0] || null;

  // Refetch data when selectedPrompt changes
  useEffect(() => {
    if (record.id && selectedPrompt) {
      refetch();
    }
  }, [selectedPrompt, record.id, refetch]);
  const expectedData = adminAnalysis?.expected_data || record.expected_data || null;
  const actualData = adminAnalysis?.real_data || record.submitted_data || null;

  const displayData = (data: any) => {
    if (!data) {
      return {};
    }
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return { value: data };
      }
    }
    return data;
  };

  // Calculate comparison result
  const comparisonResult = jsonComparator.compareJsonData(displayData(expectedData), displayData(actualData));

  const handleEditExpectedData = () => {
    setOpenDialog(true);
    setEditedExpected(JSON.stringify(expectedData || {}, null, 2));
  };

  const handleOpenUpdateDialog = () => {
    setOpenUpdateDialog(true);
    setEditedExpected(JSON.stringify(expectedData || {}, null, 2));
  };

  const handleConfirm = async () => {
    try {
      let parsedExpectedData;
      try {
        parsedExpectedData = JSON.parse(editedExpected);
      } catch (e) {
        notify('Invalid JSON', { type: 'error' });
        return;
      }

      const result = await requestAdminAnalyst({
        variables: {
          params: {
            form_submission_id: parseInt(record.id),
            form_extract_key: selectedPrompt,
            expected_data: parsedExpectedData,
          },
        },
      });

      if (result.data?.request_admin_admin_analyst?.error) {
        console.error('Mutation error:', result.data.request_admin_admin_analyst.error);
      } else {
        console.log('Admin analyst request successful');
        refetch();
      }
    } catch (error) {
      console.error('Failed to request admin analyst:', error);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleUpdateExpectedData = async () => {
    try {
      let parsedExpectedData;
      try {
        parsedExpectedData = JSON.parse(editedExpected);
      } catch (e) {
        notify('Invalid JSON', { type: 'error' });
        return;
      }

      const result = await requestUpdateAdminAnalyst({
        variables: {
          params: {
            form_submission_id: parseInt(record.id),
            form_extract_key: selectedPrompt,
            expected_data: parsedExpectedData,
          },
        },
      });

      if (result.data?.request_update_admin_analyst?.error) {
        console.error('Update mutation error:', result.data.request_update_admin_analyst.error);
        notify('更新に失敗しました', { type: 'error' });
      } else {
        console.log('Expected data update successful');
        notify('期待値が更新されました', { type: 'success' });
        refetch();
      }
    } catch (error) {
      console.error('Failed to update expected data:', error);
      notify('更新に失敗しました', { type: 'error' });
    } finally {
      setOpenUpdateDialog(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#1976d2';
    if (score >= 80) return '#ed6c02';
    return '#d32f2f';
  };

  const score = adminAnalysis?.score?.[selectedPrompt] || 0;
  return (
    <RecordContextProvider value={record}>
      <Card sx={{ border: '1px solid #e0e0e0', borderRadius: '0px' }}>
        <CardContent sx={{ p: 0 }}>
          <Grid container spacing={0}>
            {/* ID Column */}
            <Grid
              item
              xs={1}
              sx={{
                borderRight: '1px solid #e0e0e0',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                {record.id}
              </Typography>
            </Grid>

            {/* Name Column */}
            <Grid
              item
              xs={2}
              sx={{
                borderRight: '1px solid #e0e0e0',
                p: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ReferenceField source="form_template_id" reference="form_template">
                <TextField source="name" component="div" />
              </ReferenceField>
            </Grid>

            {/* Image Column */}
            <Grid
              item
              xs={2}
              sx={{
                borderRight: '1px solid #e0e0e0',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SafeAnalysisQueueImage />
            </Grid>

            {/* Expected Values Column */}
            <Grid
              item
              xs={3}
              sx={{
                borderRight: '1px solid #e0e0e0',
                p: 1,
                overflow: 'auto',
                maxHeight: '200px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
              onClick={handleOpenUpdateDialog}
            >
              <ComparisonJsonView
                src={displayData(expectedData)}
                highlightMissing={comparisonResult.missingInActual}
                type="expected"
              />
            </Grid>

            {/* Actual Results Column */}
            <Grid
              item
              xs={3}
              sx={{
                borderRight: '1px solid #e0e0e0',
                p: 1,
                overflow: 'auto',
                maxHeight: '200px',
              }}
            >
              <ComparisonJsonView
                src={displayData(actualData)}
                highlightExtra={comparisonResult.missingInExpected}
                highlightDifferent={comparisonResult.different}
                type="actual"
              />
            </Grid>

            {/* Action Column */}
            <Grid
              item
              xs={1}
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: getScoreColor(score) }}>
                {score}%
              </Typography>
              <Button variant="outlined" size="small" onClick={handleEditExpectedData}>
                実行
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>期待値の確認・編集</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            以下の期待値でテストを実行します。必要に応じて編集してください。
          </Typography>
          <JSONEditor value={editedExpected} onChange={setEditedExpected} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button onClick={handleConfirm} variant="contained" disabled={mutationLoading}>
            {mutationLoading ? '実行中...' : '実行'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Expected Data Dialog */}
      <Dialog open={openUpdateDialog} onClose={() => setOpenUpdateDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>期待値の更新</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            期待値を編集して更新してください。
          </Typography>
          <JSONEditor value={editedExpected} onChange={setEditedExpected} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpdateDialog(false)}>キャンセル</Button>
          <Button onClick={handleUpdateExpectedData} variant="contained" disabled={updateMutationLoading}>
            {updateMutationLoading ? '更新中...' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>
    </RecordContextProvider>
  );
};

const ResultListView = ({ selectedPrompt }: { selectedPrompt: SettingKeys }) => {
  const { data } = useListContext();

  return (
    <SelectedPromptContext.Provider value={selectedPrompt}>
      <Box>
        {/* Header */}
        <Card sx={{ backgroundColor: '#f5f5f5', borderRadius: '0px' }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ID
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  名前
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  画像
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  期待値
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  結果
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Action
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Data rows */}
        {data?.map((record: any) => (
          <ResultCard key={record.id} record={record} />
        ))}
      </Box>
    </SelectedPromptContext.Provider>
  );
};

const ResultListActions = ({
  selectedPrompt,
  setSelectedPrompt,
}: {
  selectedPrompt: SettingKeys;
  setSelectedPrompt: (value: SettingKeys) => void;
}) => {
  return (
    <TopToolbar sx={{ justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="body1">プロンプト:</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={selectedPrompt} onChange={(e) => setSelectedPrompt(e.target.value as SettingKeys)}>
            <MenuItem value={SettingKeys.form_extract_prompt}>Prompt ver.1.0</MenuItem>
            <MenuItem value={SettingKeys.form_extract_prompt_2_1}>Prompt ver.2.1</MenuItem>
          </Select>
        </FormControl>
        {/* <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
        >
          実行
        </Button> */}
      </Box>
    </TopToolbar>
  );
};

export const ResultList = () => {
  const [selectedPrompt, setSelectedPrompt] = useState<SettingKeys>(SettingKeys.form_extract_prompt_2_1);

  return (
    <List
      title="結果一覧"
      resource="results"
      actions={<ResultListActions selectedPrompt={selectedPrompt} setSelectedPrompt={setSelectedPrompt} />}
      filters={[]}
      perPage={10}
      sort={{ field: 'id', order: 'DESC' }}
      filterDefaultValues={{ 'form_template_id@_is_null': false, is_admin_created: true, 'deleted_at@_is_null': true }}
    >
      <ResultListView selectedPrompt={selectedPrompt} />
    </List>
  );
};

export default ResultList;
